/**
 * Filter components for the Projects page.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { FolderOpen, FileText, Layers, Search, X } from 'lucide-react';
import { Badge, Button, Input, TabsList, Tab } from '@/components/ui';
import type { ProjectConfig } from '@/lib/types';

/** Mobile filter dropdowns */
interface MobileFiltersProps {
  projects: ProjectConfig[];
  documentTypes: string[];
  selectedProjectId: string | null;
  selectedDocType: string | null;
  projectDocumentCounts: Record<string, number>;
  documentTypeCounts: Record<string, number>;
  onProjectSelect: (projectId: string | null) => void;
  onDocTypeSelect: (docType: string | null) => void;
}

export const MobileFilters: React.FC<MobileFiltersProps> = ({
  projects,
  documentTypes,
  selectedProjectId,
  selectedDocType,
  projectDocumentCounts,
  documentTypeCounts,
  onProjectSelect,
  onDocTypeSelect,
}) => {
  const { t } = useTranslation();

  return (
    <div className="md:hidden mb-4 space-y-3">
      {/* Project Selector */}
      <div className="flex items-center gap-2">
        <FolderOpen className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <select
          value={selectedProjectId || ''}
          onChange={(e) => onProjectSelect(e.target.value || null)}
          className="flex-1 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        >
          <option value="">{t('projects.allProjects')}</option>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.projectName} ({projectDocumentCounts[project.id] || 0})
            </option>
          ))}
        </select>
      </div>

      {/* Document Type Selector */}
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <select
          value={selectedDocType || ''}
          onChange={(e) => onDocTypeSelect(e.target.value || null)}
          className="flex-1 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        >
          <option value="">{t('projects.allDocumentTypes')}</option>
          {documentTypes.map((docType) => (
            <option key={docType} value={docType}>
              {docType} ({documentTypeCounts[docType] || 0})
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

/** Active filter chips */
interface ActiveFiltersProps {
  selectedProject: ProjectConfig | null;
  selectedDocType: string | null;
  onClearProject: () => void;
  onClearDocType: () => void;
  onClearAll: () => void;
}

export const ActiveFilters: React.FC<ActiveFiltersProps> = ({
  selectedProject,
  selectedDocType,
  onClearProject,
  onClearDocType,
  onClearAll,
}) => {
  const { t } = useTranslation();

  if (!selectedProject && !selectedDocType) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <span className="text-xs text-slate-500">{t('projects.activeFilters')}</span>
      {selectedProject && (
        <Badge variant="success" size="md" interactive onClick={onClearProject}>
          <FolderOpen className="w-3 h-3" />
          {selectedProject.projectName}
          <X className="w-3 h-3" />
        </Badge>
      )}
      {selectedDocType && (
        <Badge variant="secondary" size="md" interactive onClick={onClearDocType}>
          <FileText className="w-3 h-3" />
          {selectedDocType}
          <X className="w-3 h-3" />
        </Badge>
      )}
      <Button variant="ghost" size="xs" onClick={onClearAll} className="underline">
        {t('projects.clearAll')}
      </Button>
    </div>
  );
};

/** Document type tabs */
interface DocumentTypeTabsProps {
  documentTypes: string[];
  selectedDocType: string | null;
  documentTypeCounts: Record<string, number>;
  totalDocuments: number;
  onDocTypeSelect: (docType: string | null) => void;
}

export const DocumentTypeTabs: React.FC<DocumentTypeTabsProps> = ({
  documentTypes,
  selectedDocType,
  documentTypeCounts,
  totalDocuments,
  onDocTypeSelect,
}) => {
  const { t } = useTranslation();

  return (
    <TabsList variant="underlined" className="mb-4">
      {/* All */}
      <Tab variant="underlined" active={!selectedDocType} onClick={() => onDocTypeSelect(null)}>
        <Layers className="w-4 h-4" />
        {t('projects.all')}
        {totalDocuments > 0 && (
          <span
            className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
              !selectedDocType ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'
            }`}
          >
            {totalDocuments}
          </span>
        )}
      </Tab>

      {/* Document type tabs — only show types with at least 1 document */}
      {documentTypes.filter((dt) => (documentTypeCounts[dt] ?? 0) > 0).map((docType) => {
        const isActive = selectedDocType === docType;
        const count = documentTypeCounts[docType] ?? 0;

        return (
          <Tab key={docType} variant="underlined" active={isActive} onClick={() => onDocTypeSelect(docType)}>
            <FileText className="w-4 h-4" />
            {docType}
            {count > 0 && (
              <span
                className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                  isActive ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'
                }`}
              >
                {count}
              </span>
            )}
          </Tab>
        );
      })}

    </TabsList>
  );
};

/** Search input */
interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
}

export const SearchBar: React.FC<SearchBarProps> = ({ value, onChange }) => {
  const { t } = useTranslation();

  return (
    <div className="mb-5 sm:mb-6">
      <Input
        type="text"
        placeholder={t('projects.searchPlaceholder')}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        variant="ghost"
        inputSize="md"
        leftIcon={<Search className="w-4 h-4 sm:w-5 sm:h-5" />}
        rightIcon={
          value ? (
            <button
              onClick={() => onChange('')}
              className="p-1 text-slate-400 hover:text-slate-600"
              aria-label={t('projects.clearSearch')}
            >
              <X className="w-4 h-4" />
            </button>
          ) : undefined
        }
      />
    </div>
  );
};
